/**
 * Alert calls (ring R9): the defects an AI call may ring a company about, and
 * the campaign editor's preview of the openings they make.
 *
 *   POST /api/admin/crm/calls/ai-outbound/alert/opening-preview
 *        { openingQuestion } -> { variants, problems, longSeconds, defaultQuestion }
 *
 * An alert campaign changes only the question at the end of its opening. The
 * sentence about the defect is written by server code per finding, from the
 * incident a recheck saw right before dialling, so the page never builds or
 * edits it: it shows what the server built for a sample company.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/ariregister/lib/incidents/catalogue.ts`
 * and `apps/api/src/modules/crm-calls/outbound-alert.routes.ts`; `aiAlert.test.ts`
 * holds the lists below against those files, so a finding added on the server
 * fails a test here instead of rendering as a raw key.
 *
 * React-free: runs under plain node.
 */
import { api } from "./api";
import type { StatusTone } from "../crm/ui";

/** Mirrors `ALERT_FINDING_IDS`: every defect an alert call may be about. */
export const ALERT_FINDING_IDS = [
  "site-unreachable",
  "server-error",
  "tls-cert-expired",
  "domain-expiring",
  "exposed-env",
  "exposed-git",
  "exposed-wp-config-backup",
  "exposed-wp-debug-log",
  "wp-core-insecure",
  "php-eol",
  "malware-blocklisted",
  "malware-defaced",
  "link_broken",
  "layout_text_cut",
  "layout_scrolls_sideways",
] as const;
export type AlertFindingId = (typeof ALERT_FINDING_IDS)[number];

/** Mirrors `ALERT_CLASSES`. */
export const ALERT_CLASSES = ["urgent", "helpful"] as const;
export type AlertClass = (typeof ALERT_CLASSES)[number];

/** Mirrors `INCIDENT_SOURCES`: who saw the defect. */
export const INCIDENT_SOURCES = ["scanner", "watch", "brain"] as const;
export type IncidentSource = (typeof INCIDENT_SOURCES)[number];

/** Mirrors `ALERT_RECHECKS`: how the pre-dial recheck proves the defect is still there. */
export const ALERT_RECHECKS = ["homepage", "tls", "domain", "scan", "freshness", "layout"] as const;
export type AlertRecheck = (typeof ALERT_RECHECKS)[number];

/** Mirrors `ALERT_OPENING_PREVIEW_PATH`, without the `/api` prefix `api.ts` adds. */
export const ALERT_OPENING_PREVIEW_PATH = "/admin/crm/calls/ai-outbound/alert/opening-preview";

/** One finding's opening as the server built it. Mirrors `AlertOpeningVariant`. */
export interface AlertOpeningVariant {
  findingId: string;
  class: string;
  /** On a landline: no source sentence. */
  text: string;
  /** On a register mobile: with the source sentence. */
  mobileText: string;
  words: number;
  mobileWords: number;
  seconds: number;
  mobileSeconds: number;
  /** Opening check codes (`OPENING_PROBLEMS`). */
  problems: string[];
}

/** Mirrors `AlertOpeningPreview`. */
export interface AlertOpeningPreview {
  variants: AlertOpeningVariant[];
  /** What the typed question breaks in any opening. */
  problems: string[];
  /** Openings longer than this many seconds are flagged as long. */
  longSeconds: number;
  /** The question a blank field speaks. */
  defaultQuestion: string;
}

export function previewAlertOpening(openingQuestion: string): Promise<AlertOpeningPreview> {
  return api.post(ALERT_OPENING_PREVIEW_PATH, { openingQuestion });
}

/** The catalogue key of a finding's short label, or null for an id this build does not know. */
export function alertFindingKey(findingId: string): string | null {
  return (ALERT_FINDING_IDS as readonly string[]).includes(findingId) ? `siteAlert.finding.${findingId}` : null;
}

/** The catalogue key of a class, or null for one this build does not know. */
export function alertClassKey(value: string): string | null {
  return (ALERT_CLASSES as readonly string[]).includes(value) ? `siteAlert.class.${value}` : null;
}

/** An urgent defect is drawn loud, a helpful one quiet. */
export function alertClassTone(value: string): StatusTone {
  return value === "urgent" ? "lt" : "info";
}

/** Whether a variant would run past the long mark, on either phone. */
export function alertVariantLong(variant: Pick<AlertOpeningVariant, "seconds" | "mobileSeconds">, longSeconds: number): boolean {
  return Math.max(variant.seconds, variant.mobileSeconds) > longSeconds;
}
